import { Box, ArrowRight, FolderOpen, Layers, MousePointer2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n/LanguageContext';
import { formatPercent, languageToLocale } from '@/lib/locale';

interface WorkflowStatusBarProps {
  blockCount: number;
  connectorCount: number;
  groupCount: number;
  selectedBlockName?: string;
  currentLayerName?: string;
  zoom?: number;
  className?: string;
}

export function WorkflowStatusBar({
  blockCount,
  connectorCount,
  groupCount,
  selectedBlockName,
  currentLayerName,
  zoom = 1,
  className,
}: WorkflowStatusBarProps) {
  const { t, language } = useTranslation();

  return (
    <div
      className={cn(
        'h-7 flex items-center justify-between px-3 text-[11px] font-medium border-t border-border/50 bg-muted/50 text-muted-foreground select-none',
        className
      )}
      role="status"
      aria-label="Workflow statistics"
      data-testid="workflow-status-bar"
    >
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1" title={t('blocks')}>
          <Box className="w-3 h-3" aria-hidden="true" />
          {blockCount}
        </span>
        <span className="flex items-center gap-1" title="Connectors">
          <ArrowRight className="w-3 h-3" aria-hidden="true" />
          {connectorCount}
        </span>
        <span className="flex items-center gap-1" title={t('groups')}>
          <FolderOpen className="w-3 h-3" aria-hidden="true" />
          {groupCount}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {currentLayerName && (
          <span className="flex items-center gap-1 text-primary/70">
            <Layers className="w-3 h-3" aria-hidden="true" />
            {currentLayerName}
          </span>
        )}
        {selectedBlockName && (
          <span className="flex items-center gap-1">
            <MousePointer2 className="w-3 h-3" aria-hidden="true" />
            {selectedBlockName}
          </span>
        )}
        <span className="tabular-nums" title="Zoom level">
          {formatPercent(zoom, languageToLocale(language))}
        </span>
      </div>
    </div>
  );
}
